/**
 * Schwarzschild helpers in geometric units: G = c = 1, M = 1.
 * The Schwarzschild radius is 2, the photon sphere 3, the innermost stable circular orbit 6.
 * Conversions below are for Sagittarius A* (4.3 million solar masses).
 */
export const M = 1;
export const RS = 2 * M;
export const PHOTON_SPHERE = 3 * M;
export const ISCO = 6 * M;

/** GM/c^3 for Sagittarius A*: one geometric time unit in seconds. */
export const SGR_A_M_SECONDS = 21.2;
/** Schwarzschild radius of Sagittarius A* in kilometres. */
export const SGR_A_RS_KM = 12.7e6;

/** Clock rate dτ/dt of an observer hovering at radius r. */
export function staticRate(r: number): number {
  return r > RS ? Math.sqrt(1 - RS / r) : 0;
}

/** Clock rate dτ/dt of an observer on a circular orbit at radius r (valid for r > 3M). */
export function orbitRate(r: number): number {
  return r > PHOTON_SPHERE ? Math.sqrt(1 - 3 * M / r) : 0;
}

export interface InfallState {
  /** current radius */
  r: number;
  /** radius the fall started from, at rest */
  r0: number;
  /** proper time elapsed on the falling clock */
  tau: number;
  /** coordinate time elapsed (Earth's clock, to excellent approximation) */
  t: number;
  /** radial velocity dr/dτ (0 at the start) */
  v?: number;
}

/**
 * Advance a radial free fall from rest at r0 by coordinate time dt.
 * In proper time the radial equation is d²r/dτ² = -M/r² (exact in Schwarzschild), and
 * dτ/dt = (1 - 2M/r) / E with E = sqrt(1 - 2M/r0). Stops just above the horizon.
 */
export function infallStep(s: InfallState, dt: number, substeps = Math.max(16, Math.ceil(dt / 0.01))): InfallState {
  const E = Math.sqrt(Math.max(1e-12, 1 - RS / s.r0));
  let { r, tau, t } = s;
  let v = s.v ?? 0;
  const h = dt / substeps;
  for (let i = 0; i < substeps; i++) {
    if (r <= RS + 1e-4) break;
    const rate = (1 - RS / r) / E;
    const dtau = rate * h;
    v -= (M / (r * r)) * dtau;
    r = Math.max(RS + 1e-4, r + v * dtau);
    tau += dtau;
    t += h;
  }
  return { r, r0: s.r0, tau, t, v };
}

/**
 * Bending angle of light with impact parameter b (in units of M), by integrating the
 * null geodesic u'' = 3u^2 - u from infinity until it returns to infinity.
 * Returns NaN for rays that are captured (b below the critical 3*sqrt(3)).
 */
export function deflection(b: number): number {
  if (b <= 3 * Math.sqrt(3) * M) return NaN;
  let u = 0, up = 1 / b, phi = 0;
  const h = 1e-3;
  const f = (x: number) => 3 * x * x - x;
  let passedPeri = false;
  for (let i = 0; i < 400000; i++) {
    const k1u = up, k1p = f(u);
    const k2u = up + 0.5 * h * k1p, k2p = f(u + 0.5 * h * k1u);
    const k3u = up + 0.5 * h * k2p, k3p = f(u + 0.5 * h * k2u);
    const k4u = up + h * k3p, k4p = f(u + h * k3u);
    u += h / 6 * (k1u + 2 * k2u + 2 * k3u + k4u);
    up += h / 6 * (k1p + 2 * k2p + 2 * k3p + k4p);
    phi += h;
    if (up < 0) passedPeri = true;
    if (passedPeri && u <= 0) {
      // interpolate the crossing for accuracy
      const frac = u / (u - (u - up * h));
      return phi - frac * h - Math.PI;
    }
  }
  return NaN;
}

/** Radius in M units to kilometres from the centre of Sagittarius A*. */
export function toKm(r: number): number {
  return (r / RS) * SGR_A_RS_KM;
}

/** Radius in M units to Schwarzschild radii. */
export function toRs(r: number): number {
  return r / RS;
}

/** Geometric time units to seconds for Sagittarius A*. */
export function toSeconds(tGeom: number): number {
  return tGeom * SGR_A_M_SECONDS;
}

/** Human-readable duration from seconds: "2 d 4 h 07 m 30 s" style, trimmed to what matters. */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (d > 0) return `${d} d ${pad(h)} h ${pad(m)} m`;
  if (h > 0) return `${h} h ${pad(m)} m ${pad(sec)} s`;
  if (m > 0) return `${m} m ${pad(sec)} s`;
  return `${sec} s`;
}
